/**
 * #981: "Save as a copy" for a large CSV — its unsaved edits written to a NEW file beside it (spec
 * §8.5), through the existing Save As route (`keepBoth`: never onto another file). `fromBase` saves
 * the STALE edits kept on an earlier content of the file instead of the current session's.
 */
import { filesApi } from '@papan/files/client'
import { damagedMessageFrom } from '@papan/types'

export type SaveCopyResult =
  | { kind: 'saved'; name: string }
  /** Retryable: the file is being edited (`csv_changed_retry`) or a host missed the flush. */
  | { kind: 'retry' }
  /** The stale edits are already gone. */
  | { kind: 'gone' }
  /** The server would not write the copy: a value can't be encoded (`csv_unencodable`). Nothing written. */
  | { kind: 'unencodable' }
  /**
   * The kept edits name fragments no working copy of the base holds (`csv_layout_changed` — e.g. a row of the removed
   * v1 layout). Not retryable: Save refuses them the same way; only Discard clears them. Nothing written.
   */
  | { kind: 'layoutChanged' }
  /** #1397: a stored copy the copy needs (the file's own, or its `.pmeta` companion's) is damaged — the server names
   *  which in `message` (shown as it is, as every `CONTENT_DAMAGED` refusal is). Nothing written. */
  | { kind: 'damaged'; message: string }
  /** Any other failure — never carries server text (it is English; the caller words it). */
  | { kind: 'failed' }

/** `big.csv` → `big (unsaved edits).csv` (the suffix is the caller's, translated). */
export function copyName(fileName: string, suffix: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot > 0 ? `${fileName.slice(0, dot)} (${suffix})${fileName.slice(dot)}` : `${fileName} (${suffix})`
}

export async function saveLargeCsvCopy(fileId: string, parentId: string | null, name: string, fromBase?: string): Promise<SaveCopyResult> {
  try {
    const r = await filesApi.saveAs(fileId, parentId, name, true, fromBase ? { fromBase } : undefined)
    return { kind: 'saved', name: r.name }
  } catch (err) {
    const damaged = damagedMessageFrom(err)
    if (damaged) return { kind: 'damaged', message: damaged }
    const e = err as { status?: number; code?: string } | null
    if (e?.code === 'csv_changed_retry' || e?.code === 'csv_flush_timeout') return { kind: 'retry' }
    if (e?.code === 'csv_layout_changed') return { kind: 'layoutChanged' }
    if (e?.code === 'csv_base_unknown') return { kind: 'gone' }
    if (e?.code === 'csv_unencodable') return { kind: 'unencodable' }
    return { kind: 'failed' }
  }
}
